import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import './Auth.css';

const AlertIcon = () => (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
    </svg>
);

const DRAFT_KEY = 'tankly_signup_draft';

const STEP_FIELDS = {
    1: ['companyName', 'vatId', 'phoneNumber', 'street', 'city', 'zipCode', 'country'],
    2: ['firstName', 'lastName', 'email', 'password'],
};

const LABELS = {
    companyName: 'Company name', vatId: 'VAT ID', phoneNumber: 'Phone number',
    street: 'Street address', city: 'City', zipCode: 'Zip code', country: 'Country',
    firstName: 'First name', lastName: 'Last name', email: 'Email address', password: 'Password',
};

const emptyForm = {
    companyName: '', vatId: '', phoneNumber: '', street: '', city: '', zipCode: '', country: '',
    firstName: '', lastName: '', email: '', password: '',
};

const Signup = () => {
    const [step, setStep] = useState(1);
    const [formData, setFormData] = useState(() => {
        try {
            const draft = localStorage.getItem(DRAFT_KEY);
            if (draft) return { ...emptyForm, ...JSON.parse(draft) };
        } catch {
            /* corrupt draft, ignore */
        }
        return emptyForm;
    });
    const [fieldErrors, setFieldErrors] = useState({});
    const [error, setError] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const navigate = useNavigate();
    const firstFieldRef = useRef(null);

    useEffect(() => {
        try {
            // Don't persist the password in the draft
            const { password, ...rest } = formData;
            localStorage.setItem(DRAFT_KEY, JSON.stringify(rest));
        } catch {
            /* storage unavailable, skip autosave */
        }
    }, [formData]);

    useEffect(() => {
        firstFieldRef.current?.focus();
    }, [step]);

    const handleChange = (e) => {
        setFormData({ ...formData, [e.target.name]: e.target.value });
    };

    const validateStep = (n) => {
        const errs = {};
        STEP_FIELDS[n].forEach((field) => {
            if (!formData[field]?.trim()) errs[field] = `${LABELS[field]} is required.`;
        });
        if (n === 2 && formData.email && !/\S+@\S+\.\S+/.test(formData.email)) {
            errs.email = 'Enter a valid email address.';
        }
        if (n === 2 && formData.password && formData.password.length < 8) {
            errs.password = 'Password must be at least 8 characters.';
        }
        setFieldErrors(errs);
        return Object.keys(errs).length === 0;
    };

    const handleBlur = (field) => {
        if (fieldErrors[field]) validateStep(step);
    };

    const goNext = () => {
        if (validateStep(1)) setStep(2);
    };

    const goBack = () => setStep(1);

    const handleSignup = async (e) => {
        e.preventDefault();
        if (!validateStep(2)) return;
        setIsLoading(true);
        setError('');
        try {
            const apiUrl = process.env.REACT_APP_API_URL || 'http://localhost:8080';
            await axios.post(`${apiUrl}/api/v1/auth/register`, formData);
            try { localStorage.removeItem(DRAFT_KEY); } catch { /* ignore */ }
            navigate('/login', { state: { justRegistered: true } });
        } catch (err) {
            setError(err.response?.data || 'Registration failed. Please check your inputs and try again.');
            setIsLoading(false);
        }
    };

    const renderField = (name, type = 'text', autoComplete, full) => (
        <div className={`field${full ? ' full' : ''}`}>
            <label htmlFor={`signup-${name}`}>
                {LABELS[name]} <span className="required" aria-hidden="true">*</span>
            </label>
            <input
                id={`signup-${name}`}
                ref={STEP_FIELDS[step][0] === name ? firstFieldRef : undefined}
                name={name}
                type={type}
                autoComplete={autoComplete}
                value={formData[name]}
                onChange={handleChange}
                onBlur={() => handleBlur(name)}
                aria-invalid={!!fieldErrors[name]}
                aria-describedby={fieldErrors[name] ? `${name}-error` : undefined}
                required
            />
            {fieldErrors[name] && (
                <span className="field-error" id={`${name}-error`} role="alert">
                    <AlertIcon /> {fieldErrors[name]}
                </span>
            )}
        </div>
    );

    return (
        <div className="auth-shell">
            <section className="auth-brand" aria-label="About Tankly">
                <img className="auth-brand-logo" src="/tankly_logo.svg" alt="Tankly" />
                <div className="auth-brand-copy">
                    <h1>Set up your fleet in minutes.</h1>
                    <p>Add your company once. Every driver and route benefits from Tankly's fuel savings from day one.</p>
                </div>
                <div className="auth-brand-stat">
                    <div><strong>2 steps</strong><span>To get set up</span></div>
                    <div><strong>~3 min</strong><span>Average signup time</span></div>
                </div>
            </section>

            <section className="auth-form-side">
                <div className="auth-card auth-card-wide">
                    <h2>Register your company</h2>
                    <p className="auth-subtitle">Join Tankly and start optimizing your fleet's fuel spend.</p>

                    <div className="step-progress" role="group" aria-label="Signup progress">
                        <span className={`step-dot ${step === 1 ? 'active' : 'done'}`} aria-current={step === 1 ? 'step' : undefined}>
                            {step > 1 ? '✓' : '1'}
                        </span>
                        <span className="step-label">Company</span>
                        <span className="step-track" aria-hidden="true" />
                        <span className={`step-dot ${step === 2 ? 'active' : ''}`} aria-current={step === 2 ? 'step' : undefined}>2</span>
                        <span className="step-label">Admin user</span>
                    </div>

                    {error && (
                        <div className="alert alert-error" role="alert" aria-live="assertive">
                            <AlertIcon /><span>{error}</span>
                        </div>
                    )}

                    <form onSubmit={handleSignup} noValidate>
                        {step === 1 && (
                            <fieldset className="form-section">
                                <legend>Company details</legend>
                                <div className="field-grid">
                                    {renderField('companyName')}
                                    {renderField('vatId')}
                                    {renderField('phoneNumber', 'tel', 'tel')}
                                    {renderField('street', 'text', 'street-address')}
                                    {renderField('city', 'text', 'address-level2')}
                                    {renderField('zipCode', 'text', 'postal-code')}
                                    <div className="field full">
                                        <label htmlFor="signup-country">
                                            Country <span className="required" aria-hidden="true">*</span>
                                        </label>
                                        <input
                                            id="signup-country"
                                            name="country"
                                            autoComplete="country-name"
                                            value={formData.country}
                                            onChange={handleChange}
                                            onBlur={() => handleBlur('country')}
                                            aria-invalid={!!fieldErrors.country}
                                            required
                                        />
                                        {fieldErrors.country && (
                                            <span className="field-error" role="alert"><AlertIcon /> {fieldErrors.country}</span>
                                        )}
                                    </div>
                                </div>
                                <button type="button" className="btn-primary" onClick={goNext}>Continue</button>
                            </fieldset>
                        )}

                        {step === 2 && (
                            <fieldset className="form-section">
                                <legend>Admin user details</legend>
                                <div className="field-grid">
                                    {renderField('firstName', 'text', 'given-name')}
                                    {renderField('lastName', 'text', 'family-name')}
                                    {renderField('email', 'email', 'email', true)}
                                    <div className="field full">
                                        <label htmlFor="signup-password">
                                            Password <span className="required" aria-hidden="true">*</span>
                                        </label>
                                        <input
                                            id="signup-password"
                                            name="password"
                                            type="password"
                                            autoComplete="new-password"
                                            value={formData.password}
                                            onChange={handleChange}
                                            onBlur={() => handleBlur('password')}
                                            aria-invalid={!!fieldErrors.password}
                                            aria-describedby="password-hint"
                                            required
                                        />
                                        <span className="hint" id="password-hint">At least 8 characters.</span>
                                        {fieldErrors.password && (
                                            <span className="field-error" role="alert"><AlertIcon /> {fieldErrors.password}</span>
                                        )}
                                    </div>
                                </div>
                                <div style={{ display: 'flex', gap: 12 }}>
                                    <button type="button" className="btn-secondary" onClick={goBack}>Back</button>
                                    <button type="submit" className="btn-primary" disabled={isLoading}>
                                        {isLoading && <span className="spinner-icon" aria-hidden="true" />}
                                        {isLoading ? 'Creating account…' : 'Create account'}
                                    </button>
                                </div>
                            </fieldset>
                        )}
                    </form>

                    <div className="auth-footer">
                        Already registered? <Link to="/login">Sign in here</Link>
                    </div>
                </div>
            </section>
        </div>
    );
};

export default Signup;
